import { OpenAPI, User } from '@/generated/api';

const IMPERSONATED_USER_KEY = 'impersonatedUser';
const IMPERSONATION_HEADER = 'of-impersonated-user';

/**
 * Gets the impersonated user from localStorage with security validation
 * @param currentUser The current authenticated user (optional for backward compatibility)
 * @returns The parsed User object or null if not found or unauthorized
 */
export function getImpersonatedUserFromStorage(
  currentUser?: User | null,
): User | null {
  // Security check: only admins can access impersonation data
  if (currentUser && currentUser.role !== User.role._0) {
    clearImpersonatedUser(); // Clean up any existing data
    return null;
  }

  const impersonatedUserFromLocalStorage = localStorage.getItem(
    IMPERSONATED_USER_KEY,
  );

  if (impersonatedUserFromLocalStorage) {
    try {
      return JSON.parse(impersonatedUserFromLocalStorage) as User;
    } catch {
      // If parsing fails, remove the corrupted data
      localStorage.removeItem(IMPERSONATED_USER_KEY);
      return null;
    }
  }

  return null;
}

/**
 * Sets the impersonated user in localStorage and updates OpenAPI headers
 * @param user The user to impersonate
 */
export function setImpersonatedUser(user: User): void {
  localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify(user));
  OpenAPI.HEADERS = {
    ...OpenAPI.HEADERS,
    [IMPERSONATION_HEADER]: user.id.toString(),
  };
}

/**
 * Clears the impersonated user from localStorage and removes the header
 */
export function clearImpersonatedUser(): void {
  localStorage.removeItem(IMPERSONATED_USER_KEY);

  if (OpenAPI.HEADERS) {
    const { [IMPERSONATION_HEADER]: _, ...rest } = OpenAPI.HEADERS as Record<
      string,
      string
    >;
    OpenAPI.HEADERS = rest;
  }
}

/**
 * Sets the impersonation header without modifying localStorage
 * Used when initializing from existing localStorage data
 * @param user The user to set in headers
 */
export function setImpersonationHeader(user: User): void {
  OpenAPI.HEADERS = {
    ...OpenAPI.HEADERS,
    [IMPERSONATION_HEADER]: user.id.toString(),
  };
}
